import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import WorkArt from '@/components/landing/work-art'

interface ProjectCardProps {
    project: Project
    index: number
    className?: string
    headingLevel?: 'h2' | 'h3'
}

// One project: the preview un-clips as it comes into view, with the client's name and story underneath.
const ProjectCard: FC<ProjectCardProps> = ({ project, index, className, headingLevel = 'h3' }): ReactNode => {
    const Heading = headingLevel

    return (
        <article className={cn('group', className)}>
            <motion.div
                initial={{ clipPath: 'inset(12% 9% 12% 9% round 8px)' }}
                whileInView={{ clipPath: 'inset(0% 0% 0% 0% round 8px)' }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
                <WorkArt
                    project={project}
                    className='transition-[translate,rotate] duration-500 ease-snap group-hover:-translate-y-1 group-hover:-rotate-[0.6deg]'
                />
            </motion.div>

            <div className='mt-6 grid gap-2 sm:grid-cols-[3rem_1fr] sm:gap-6'>
                <span className='eyebrow pt-2 opacity-60'>
                    {String(index + 1).padStart(2, '0')}
                </span>

                <div>
                    <Heading className='font-heading text-3xl font-extrabold leading-[0.95] tracking-[-0.03em] wdth-80 sm:text-4xl'>
                        {project.name}
                    </Heading>

                    <p className='mt-3 max-w-[38rem] text-base leading-relaxed opacity-85 sm:text-lg'>
                        {project.description}
                    </p>
                </div>
            </div>
        </article>
    )
}

export default ProjectCard
